import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { authFetch, buildQuery } from "../../lib/api"

const CATEGORIES = ["For Sale", "For Rent"]

const initialForm = {
  name: "",
  location: "",
  category: "For Rent",
  type: "Apartment",
  price: "",
  cover: "",
  gallery: "",
  beds: "",
  baths: "",
  area: "",
  description: "",
  isShortTerm: false,
}

const AdminListings = () => {
  const [listings, setListings] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [loadError, setLoadError] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(initialForm)

  useEffect(() => {
    let cancelled = false
    setLoadingList(true)
    setLoadError("")
    authFetch(`/api/listings${buildQuery({ limit: 200 })}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        if (data.error) setLoadError(data.error)
        else if (data.listings) setListings(data.listings)
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError("Could not connect to the API. Is the API server running? Run npm run start:api in a separate terminal.")
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadListingIntoForm = (listing) => {
    setEditingId(listing.id)
    setForm({
      name: listing.name || "",
      location: listing.location || "",
      category: listing.category || "For Rent",
      type: listing.type || "Apartment",
      price: listing.price || "",
      cover: listing.cover || "",
      gallery: Array.isArray(listing.gallery) ? listing.gallery.join("\n") : "",
      beds: listing.beds != null ? String(listing.beds) : "",
      baths: listing.baths != null ? String(listing.baths) : "",
      area: listing.area || "",
      description: listing.description || "",
      isShortTerm: Boolean(listing.isShortTerm),
    })
  }

  const clearForm = () => {
    setEditingId(null)
    setForm(initialForm)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSaving(true)
    const galleryArr = form.gallery
      .split("\n")
      .map((value) => value.trim())
      .filter(Boolean)
    const body = {
      name: form.name.trim(),
      location: form.location.trim(),
      category: form.category,
      type: form.type.trim(),
      price: form.price.trim(),
      cover: form.cover.trim(),
      gallery: galleryArr,
      beds: form.beds === "" ? undefined : Number(form.beds),
      baths: form.baths === "" ? undefined : Number(form.baths),
      area: form.area.trim() || undefined,
      description: form.description.trim() || undefined,
      isShortTerm: form.isShortTerm,
    }
    try {
      if (editingId) {
        const res = await authFetch(`/api/listings/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (!res.ok) {
          setError(data.error || "Update failed")
          return
        }
        setListings((prev) => prev.map((listing) => (listing.id === editingId ? data : listing)))
        clearForm()
      } else {
        const res = await authFetch("/api/listings", {
          method: "POST",
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (!res.ok) {
          setError(data.error || "Create failed")
          return
        }
        setListings((prev) => [data, ...prev])
        clearForm()
      }
    } catch (err) {
      setError("Request failed")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this listing?")) return
    setError("")
    try {
      const res = await authFetch(`/api/listings/${id}`, { method: "DELETE" })
      if (!res.ok) {
        const data = await res.json()
        setError(data.error || "Delete failed")
        return
      }
      setListings((prev) => prev.filter((listing) => listing.id !== id))
      if (editingId === id) clearForm()
    } catch (err) {
      setError("Request failed")
    }
  }

  return (
    <div className='dashboard-stack'>
      <section className='dashboard-card'>
        <div className='dashboard-card__header'>
          <div>
            <span className='dashboard-eyebrow'>Inventory</span>
            <h2>{editingId ? "Edit listing" : "Add listing"}</h2>
            <p>Create and maintain the homes shown on the public listings page.</p>
          </div>
        </div>

        {error && <p className='dashboard-error'>{error}</p>}

        <form onSubmit={handleSubmit} className='admin-form admin-form--wide'>
          <label>
            Name *
            <input name='name' value={form.name} onChange={handleChange} required />
          </label>
          <label>
            Location *
            <input name='location' value={form.location} onChange={handleChange} required />
          </label>
          <label>
            Category *
            <select name='category' value={form.category} onChange={handleChange}>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </label>
          <label>
            Type *
            <input name='type' value={form.type} onChange={handleChange} required />
          </label>
          <label>
            Price * (e.g. $3,700)
            <input name='price' value={form.price} onChange={handleChange} required />
          </label>
          <label>
            Cover image URL *
            <input name='cover' value={form.cover} onChange={handleChange} required />
          </label>
          <label>
            Beds
            <input name='beds' type='number' min='0' value={form.beds} onChange={handleChange} />
          </label>
          <label>
            Baths
            <input name='baths' type='number' min='0' value={form.baths} onChange={handleChange} />
          </label>
          <label>
            Area
            <input name='area' value={form.area} onChange={handleChange} />
          </label>
          <label className='admin-form__full'>
            Gallery URLs (one per line)
            <textarea name='gallery' value={form.gallery} onChange={handleChange} rows={3} />
          </label>
          <label className='admin-form__full'>
            Description
            <textarea name='description' value={form.description} onChange={handleChange} rows={4} />
          </label>
          <label className='admin-form__full'>
            <span>
              <input name='isShortTerm' type='checkbox' checked={form.isShortTerm} onChange={handleChange} /> Short-term / Airbnb style
            </span>
          </label>
          <div className='admin-form__actions'>
            <button type='submit' className='dashboard-primary-action admin-primary-action' disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update listing" : "Add listing"}
            </button>
            {editingId && (
              <button type='button' className='btn btn--secondary' onClick={clearForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>
      </section>

      <section className='dashboard-card'>
        <div className='dashboard-card__header'>
          <div>
            <span className='dashboard-eyebrow'>Published inventory</span>
            <h2>Existing listings</h2>
          </div>
        </div>

        {loadError && <p className='dashboard-error'>{loadError}</p>}
        {loadingList ? (
          <p>Loading...</p>
        ) : listings.length === 0 && !loadError ? (
          <div className='dashboard-empty'>No listings yet. Add one above.</div>
        ) : !loadError ? (
          <div className='admin-table-wrap'>
            <table className='admin-table'>
              <thead>
                <tr>
                  <th>Listing</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => (
                  <tr key={listing.id}>
                    <td>
                      <Link to={`/listings/${listing.id}`}>{listing.name}</Link>
                      <div className='admin-table__muted'>{listing.location}</div>
                    </td>
                    <td>{listing.category}</td>
                    <td>{listing.price}</td>
                    <td>
                      <div className='dashboard-list__actions'>
                        <button type='button' className='btn btn--secondary' onClick={() => loadListingIntoForm(listing)}>
                          Edit
                        </button>
                        <button type='button' className='btn btn--danger' onClick={() => handleDelete(listing.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  )
}

export default AdminListings
